/**
 * useBookingFlow Hook
 *
 * Centralized booking logic with subscription-aware flow and error handling
 *
 * Usage:
 * const {
 *   bookingError,
 *   isLoading,
 *   buttonState,
 *   handleBooking,
 *   resetBookingError
 * } = useBookingFlow();
 */

import { useState, useCallback, useMemo } from "react";
import { useSubscription } from "./useSubscription";
import { getAnalytics, logEvent } from "firebase/analytics";
import spaceManagementService from "../services/host-apiServices/spaceManagementService";
import * as localService from "../services/localService";
import {
  isBookingError,
  getBookingErrorMessage,
  validateBookingPayload,
  getBookingButtonsVisibility,
} from "../utils/bookingUtils";
import logger from "../utils/logger";
import { initiateBookingPayment } from "../utils/paymentFlowHelper";
import toastUtils from "../utils/toast";

// Helper to log booking flow debug events to Firebase Analytics
const logBookingFlowDebug = (eventName, data) => {
  try {
    const analytics = getAnalytics();
    if (analytics) {
      logEvent(analytics, eventName, {
        ...data,
        timestamp: Date.now(),
        source: "useBookingFlow",
      });
    }
  } catch (e) {
    logger.log("Firebase Analytics not available for logging");
  }
  // Also log to console for local debugging
  logger.log(`🔄 BOOKING FLOW [${eventName}]:`, data);
};

export const useBookingFlow = () => {
  const { activeSubscription, hasActiveSubscription } = useSubscription();
  const [bookingError, setBookingError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Check if current user has a valid subscription for booking
  // For protected roles (Individual, Subscriber, Corporate), subscription MUST be recurring
  const isSubscriptionValidForBooking = useMemo(() => {
    if (!hasActiveSubscription) return false;

    try {
      // Get current user to check organization type
      const user = localService.getUser();
      const orgType = user?.organization?.type?.code || user?.organizationType?.code;

      // List of organization types that require RECURRING subscription to book
      const RESTRICTED_ORG_TYPES = [
        "INDIVIDUAL_CUSTOMER",
        "SUBSCRIBER_CUSTOMER",
        "SUBSCRIBER_INDIVIDUAL",
        "CORPORATE_CUSTOMER",
      ];

      // If user is in restricted list, they MUST have a recurring subscription
      if (RESTRICTED_ORG_TYPES.includes(orgType)) {
        const isRecurring = activeSubscription?.recurring === true;
        logger.log(`Subscription check for ${orgType}: Active=${hasActiveSubscription}, Recurring=${isRecurring}`);
        return isRecurring;
      }

      // For other roles (e.g. Host), just having an active subscription is enough
      return hasActiveSubscription;
    } catch (error) {
      logger.error("Error checking subscription validity:", error);
      return hasActiveSubscription; // Fallback to basic check
    }
  }, [hasActiveSubscription, activeSubscription]);

  /**
   * Get button visibility state
   * Use the STRICT validation check
   */
  const buttonState = getBookingButtonsVisibility(isSubscriptionValidForBooking, bookingError);

  /**
   * Reset booking error state
   */
  const resetBookingError = useCallback(() => {
    setBookingError(false);
    setErrorMessage(null);
  }, []);

  /**
   * Main booking handler with subscription-aware logic
   *
   * Flow:
   * 1. Validate payload
   * 2. If has subscription and no previous error: try createBooking
   * 3. If no subscription or previous error: use createBookingPendingPayment
   * 4. On error (4xx/5xx): set error state to show pay button
   *
   * @param {Object} payload - Booking payload
   * @param {Object} options - Options
   * @param {boolean} options.forcePay - Force payment flow even with subscription
   * @param {Object} options.space - Space/workspace object (required for payment flow)
   * @param {Function} options.onSuccess - Success callback
   * @param {Function} options.onError - Error callback
   * @returns {Promise<Object>} - Booking response
   */
  const handleBooking = useCallback(
    async (payload, options = {}) => {
      const { forcePay = false, space, quantity = 1, onSuccess, onError } = options;

      setIsLoading(true);
      setErrorMessage(null);

      try {
        // Validate payload
        const validation = validateBookingPayload(payload);
        if (!validation.isValid) {
          const validationError = validation.errors.join(", ");
          logger.error("Booking validation failed:", validation.errors);
          setErrorMessage(validationError);
          if (onError) onError(new Error(validationError));
          setIsLoading(false);
          return { success: false, error: validationError };
        }

        let response;

        // Determine which API to call
        // forcePay takes precedence - if explicitly requested, use payment flow
        if (forcePay) {
          logger.log("Using payment flow (forcePay=true)");
          response = await spaceManagementService.createBookingPendingPayment(payload);
          logger.log("Payment booking successful:", response);
        } else if (isSubscriptionValidForBooking && !bookingError) {
          // Try subscription-based booking only if VALID active subscription (recurring checked)
          logger.log("Attempting subscription-based booking");
          response = await spaceManagementService.createBooking(payload);
          logger.log("Subscription booking successful:", response);
        } else {
          // Fallback to payment flow if no valid subscription or previous errors
          logger.log("Using payment flow (fallback - no valid subscription or error)");
          response = await spaceManagementService.createBookingPendingPayment(payload);
          logger.log("Payment booking successful:", response);
        }

        // Success
        setBookingError(false);
        setErrorMessage(null);
        setIsLoading(false);

        let paymentResult = null;

        // If this was a pending payment booking (forcePay=true), initiate payment flow
        if (forcePay && space) {
          // Extract booking from response
          const booking = response.data?.data || response.data;

          // 🔍 Log before initiating payment
          logBookingFlowDebug("handleBooking_payment_initiation", {
            bookingId: booking?.id,
            spaceId: space?.id,
            spaceName: space?.name || space?.title,
            quantity,
            priceSource: space?.propertyPrice?.price
              ? "propertyPrice.price"
              : space?.price
                ? "price"
                : space?.ratePrice
                  ? "ratePrice"
                  : "unknown",
            rawPriceFields: {
              propertyPricePrice: space?.propertyPrice?.price,
              spacePrice: space?.price,
              ratePrice: space?.ratePrice,
            },
            startTime: booking?.startTime,
            endTime: booking?.endTime,
          });

          // Handle special case for PLAY service tickets
          const isPlayService = space?.category?.name === "Play" || booking?.tickets?.length > 0;
          if (isPlayService && booking?.tickets?.length > 0) {
            const ticket = booking.tickets[0];
            // If it's a DEFAULT ticket, proceed to normal payment flow
            if (ticket.ticketType !== "DEFAULT" && ticket.url) {
              let ticketUrl = ticket.url;
              // Ensure absolute URL to prevent appending to base URL
              if (!ticketUrl.startsWith("http://") && !ticketUrl.startsWith("https://")) {
                ticketUrl = "https://" + ticketUrl;
              }
              logger.log("Play service detected, redirecting to ticket URL:", ticketUrl);
              if (onSuccess) {
                onSuccess(response);
              }
              return { success: true, data: response, isPlayService: true, ticketUrl };
            }
          }

          try {
            // Initiate payment flow (create invoice and initiate payment)
            paymentResult = await initiateBookingPayment(booking, space, quantity);
          } catch (paymentError) {
            // Payment initiation failed, but booking was created successfully
            logger.error("Payment initiation failed:", paymentError);
            toastUtils.warning("Booking created", {
              description: "Please complete payment from your bookings page to confirm your booking.",
            });
            paymentResult = { success: false, error: paymentError };
            // Still go through with onSuccess since booking was created
          }
        }

        if (onSuccess) {
          onSuccess(response);
        }

        return { success: true, data: response, paymentResult };
      } catch (error) {
        logger.error("Booking error:", error);
        setIsLoading(false);

        if (isBookingError(error)) {
          setBookingError(true);
          const message = getBookingErrorMessage(error);
          setErrorMessage(message);

          if (onError) {
            onError(error);
          }

          return { success: false, error: message, shouldShowPayButton: true };
        }

        setBookingError(true);
        const message = getBookingErrorMessage(error);
        setErrorMessage(message);

        if (onError) {
          onError(error);
        }

        return { success: false, error: message, shouldShowPayButton: true };
      }
    },
    [isSubscriptionValidForBooking, bookingError]
  );

  /**
   * Handle booking with subscription flow (ALWAYS uses regular booking API, never payment)
   * This is for the blue "Book Now" button - it must NEVER call pending payment API
   */
  const handleBookNow = useCallback(async (payload, options = {}) => {
    const { onSuccess, onError } = options;

    setIsLoading(true);
    setErrorMessage(null);

    try {
      // Validate payload
      const validation = validateBookingPayload(payload);
      if (!validation.isValid) {
        const validationError = validation.errors.join(", ");
        logger.error("Booking validation failed:", validation.errors);
        setErrorMessage(validationError);
        if (onError) onError(new Error(validationError));
        setIsLoading(false);
        return { success: false, error: validationError };
      }

      // ALWAYS call regular booking API - NEVER pending payment
      logger.log("Calling regular booking API (handleBookNow - no fallback to payment)");
      const response = await spaceManagementService.createBooking(payload);
      logger.log("Regular booking successful:", response);

      // Success
      setBookingError(false);
      setErrorMessage(null);
      setIsLoading(false);

      if (onSuccess) {
        onSuccess(response);
      }

      return { success: true, data: response };
    } catch (error) {
      logger.error("Booking error:", error);
      setIsLoading(false);

      const message = getBookingErrorMessage(error);
      setErrorMessage(message);
      setBookingError(true);

      if (onError) {
        onError(error);
      }

      return { success: false, error: message };
    }
  }, []);

  /**
   * Handle booking with payment flow (goes straight to payment)
   */
  const handlePayNow = useCallback(
    async (payload, options = {}) => {
      return handleBooking(payload, { ...options, forcePay: true });
    },
    [handleBooking]
  );

  /**
   * Handle batch booking (multiple units)
   */
  const handleBatchBookNow = useCallback(async (payloads, options = {}) => {
    const { onSuccess, onError } = options;
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const results = [];
      for (const payload of payloads) {
        const response = await spaceManagementService.createBooking(payload);
        results.push(response);
      }

      setBookingError(false);
      setIsLoading(false);
      if (onSuccess) onSuccess(results);
      return { success: true, data: results };
    } catch (error) {
      logger.error("Batch booking error:", error);
      setIsLoading(false);
      const message = getBookingErrorMessage(error);
      setErrorMessage(message);
      setBookingError(true);
      if (onError) onError(error);
      return { success: false, error: message };
    }
  }, []);

  /**
   * Handle batch payment flow
   */
  const handleBatchPayNow = useCallback(async (payloads, options = {}) => {
    const { space, quantity = 1, onSuccess, onError } = options;
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const bookings = [];
      for (const payload of payloads) {
        const response = await spaceManagementService.createBookingPendingPayment(payload);
        bookings.push(response.data?.data || response.data);
      }

      if (bookings.length === 0) throw new Error("No bookings created");

      // 🔍 Log before initiating batch payment
      logBookingFlowDebug("handleBatchPayNow_payment_initiation", {
        bookingsCount: bookings.length,
        firstBookingId: bookings[0]?.id,
        spaceId: space?.id,
        spaceName: space?.name || space?.title,
        quantity,
        priceSource: space?.propertyPrice?.price
          ? "propertyPrice.price"
          : space?.price
            ? "price"
            : space?.ratePrice
              ? "ratePrice"
              : "unknown",
        rawPriceFields: {
          propertyPricePrice: space?.propertyPrice?.price,
          spacePrice: space?.price,
          ratePrice: space?.ratePrice,
        },
        startTime: bookings[0]?.startTime,
        endTime: bookings[0]?.endTime,
      });

      // Initiate ONE payment for ALL bookings
      // Use the first booking as a reference but pass the total quantity
      const paymentResult = await initiateBookingPayment(bookings[0], space, quantity);

      setBookingError(false);
      setIsLoading(false);
      if (onSuccess) onSuccess({ bookings, paymentResult });
      return { success: true, bookings, paymentResult };
    } catch (error) {
      logger.error("Batch payment error:", error);
      setIsLoading(false);
      const message = getBookingErrorMessage(error);
      setErrorMessage(message);
      setBookingError(true);
      if (onError) onError(error);
      return { success: false, error: message };
    }
  }, []);

  return {
    // State
    bookingError,
    errorMessage,
    isLoading,
    // Return the VALIDATED subscription status, not just raw existence
    // This ensures consumers see "no subscription" if it's not recurring (for restricted roles)
    hasActiveSubscription: isSubscriptionValidForBooking,

    // Original raw value if needed for other checks
    rawHasActiveSubscription: hasActiveSubscription,

    // Button visibility
    buttonState,

    // Actions
    handleBooking,
    handleBookNow,
    handlePayNow,
    handleBatchBookNow,
    handleBatchPayNow,
    resetBookingError,
  };
};

export default useBookingFlow;
